import { atom, computed } from 'nanostores';
import { toast } from 'react-toastify';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import type { PricingMembershipState } from '~/lib/stores/pricing/types';
import { userStore } from '~/lib/stores/user';

import { personalPricingList, type Pricing } from '~/utils/pricing';

class PersonalPricingSettingsStore {
  membership = atom<PricingMembershipState | null>(null);

  selectedPricing = atom<Pricing>(personalPricingList[0]);

  activePricing = atom<Pricing | null>(null);

  nextHigherTierPricing = computed([this.activePricing], (activePricing) => {
    if (!activePricing) {
      return null;
    }

    const nextTier = personalPricingList.find((pricing) => pricing.tier === activePricing.tier + 1);

    return nextTier ?? null;
  });

  isLoading = atom(false);

  async upgradeToNextTier() {
    const nextTierPricing = this.nextHigherTierPricing.get();

    if (!nextTierPricing) {
      toast.error('You are already on the highest tier');
      return;
    }

    pricingStore.updatePlanScreenData.set({
      updateType: 'upgrade',
      pricing: nextTierPricing,
      interval: this.membership.get()?.subscription?.interval ?? 'monthly',
    });

    return;
  }
}

export const personalPricingSettingsStore = new PersonalPricingSettingsStore();

userStore.user.subscribe((user) => {
  if (!user?.membership) {
    return;
  }

  const tier = user.membership.tier ?? null;
  const nextTier = user.membership.nextTier ?? null;

  personalPricingSettingsStore.membership.set({
    tier,
    nextTier,
    nextInterval: user.membership.nextInterval,
    subscription: user.membership.subscription,
  });

  if (tier) {
    personalPricingSettingsStore.activePricing.set(
      personalPricingList.find((pricing) => pricing.tier === tier) ?? null,
    );
  }
});

if (import.meta.env.DEV) {
  Object.assign(globalThis, { personalPricingSettingsStore });
}
